// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Every user-facing block of prose, in one file.
 *
 * Keeping it here rather than scattered through ui.ts means the honesty gates
 * — what this tool may and may not claim — can be read in one sitting. The
 * rules, from plan.md: no claim about whether an image was AI-generated; the
 * ComfyUI column is a prediction and is dated; no audience figure; no
 * "displaces" claim; the word "clean" appears nowhere.
 */

import { COMFY_READER_DATE, COMFY_READER_SOURCE } from './ops/verify.ts';
import { term } from './glossary.ts';

export const HOW_IT_WORKS = `
<p>A generation recipe can be hiding in five different places in a PNG, and most readers look in one.
regraft looks in all five, tells you which one it found, and can write the answer back into the file in
the form the reader you care about will actually reach.</p>

<ol class="steps">
  <li><strong>It walks the whole file.</strong> From the PNG signature to the last byte — not just up to
  the picture. Each ${term('chunk')} is checked against its own ${term('crc', 'checksum')}, and a chunk
  that fails is still read rather than thrown away, because damage to the image data has nothing to do
  with the text sitting beside it.</li>

  <li><strong>It reads all four text carriers.</strong> ${term('text', 'tEXt, zTXt and iTXt')}, plus the
  private <code>comf</code> chunk ComfyUI's animated-PNG writer emits. A recipe written after the
  ${term('idat', 'image data')} is perfectly legal and very commonly missed; regraft reports where each
  one sat.</li>

  <li><strong>It looks in the pixels.</strong> If there is no text chunk at all, the settings may still be
  written one bit at a time into ${term('lsb', 'the low bit of each sample')} — the way NovelAI does by
  default and Forge does on request. regraft streams just the rows it needs to find the signature, then
  just the columns the payload spans, so a 60-megapixel image does not have to be held in memory.</li>

  <li><strong>You choose what goes back.</strong> A ${term('graft')} writes the recipes you pick as text
  chunks placed immediately after the header, replacing any older copy of the same keyword. The picture
  itself is copied across byte for byte — nothing is re-encoded, so there is no quality loss of any kind.</li>

  <li><strong>It re-reads what it just wrote.</strong> A fresh parser walks the output as if it had never
  seen it, confirms the image data is identical to the input, and runs an emulation of ComfyUI's real
  reader over the result to say what that reader will and will not find. Every run also produces a
  report — including a run that found nothing, which is a real answer to a real question.</li>
</ol>

<h3>What it will not do</h3>
<ul>
  <li>It will not tell you whether an image was made by a model. It reports what a file says about
  itself. Anyone can write any chunk into any PNG, so finding a graph proves nothing — and platforms
  strip these on upload, so finding none proves nothing either.</li>
  <li>It does not remove metadata. That is a different job and
  <a href="https://metascrub.benrichardson.dev" target="_blank" rel="noopener">metascrub</a> already does
  it properly, for these exact file types.</li>
  <li>PNG only, for now. A JPEG cannot carry PNG text chunks, and re-saving as a JPEG is usually what
  destroyed the recipe in the first place.</li>
</ul>
`;

export const PRIVACY = `
<p>Your image is opened, read and rewritten inside this browser tab. It is never uploaded, because there
is nowhere to upload it to — this page has no server behind it and makes no request of its own.</p>

<h3>Protected</h3>
<ul class="trust-list trust-yes">
  <li>The image never leaves your device. A build gate blocks <code>fetch</code> with a body,
  <code>FormData</code>, <code>XMLHttpRequest</code>, <code>sendBeacon</code> and WebSockets from
  shipping in this tool's own code.</li>
  <li>Recovered prompts — which routinely name a client, a person, or a folder on your machine — are
  rendered into your own page and nowhere else. They are never logged, never stored and never sent.
  Every <code>console</code> call is stripped at build time so a prompt cannot leak into a shared
  machine's developer tools.</li>
  <li>Nothing is written to disk. No cookies, no IndexedDB, no Origin Private File System, no
  localStorage. The build gate asserts that none of those appear in the shipped code.</li>
  <li>It works with the network switched off. After the first visit the whole tool, including the
  sample images, is served from your browser's own cache.</li>
</ul>

<h3>Not protected</h3>
<ul class="trust-list trust-no">
  <li><strong>A grafted file contains the recipe by design.</strong> That is the entire point of the
  feature. If you then post that file somewhere, you have posted the prompt, the model names and any
  file paths inside it.</li>
  <li><strong>regraft is not a metadata remover.</strong> It does not touch camera EXIF, GPS
  coordinates, timestamps, colour profiles or C2PA content credentials, and it never claims a file is
  free of them. A file regraft has touched may still be carrying a great deal.</li>
  <li><strong>Overwriting the pixel plane changes the picture.</strong> Each affected sample moves by one
  level out of 256 — invisible, but real. The report states exactly how many samples changed.</li>
  <li><strong>An absent recipe proves nothing.</strong> Chat apps, forums and any re-encode strip text
  chunks on upload, and a re-encode destroys the hidden pixel plane too.</li>
</ul>

<h3>Trust surface</h3>
<ul class="trust-list trust-eye">
  <li>This static bundle, served by GitHub Pages over its TLS chain, and the copy of it cached by your
  own browser.</li>
  <li>Your browser's own Compression Streams implementation, which does the decompressing.</li>
  <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no
  cross-site tracking; your files and data are never sent to it.</li>
  <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open
  the feedback form and press Send; your files and data never are.</li>
</ul>

<h3>The prediction is a prediction</h3>
<p>The "what ComfyUI will see" column is produced by an emulation of that application's own reader as it
stood on ${COMFY_READER_DATE}, written from <code>${COMFY_READER_SOURCE}</code>. It reproduces that
reader's real behaviour, including two of its bugs. It is not a test against a running copy of ComfyUI,
and if that reader changes this prediction will drift.</p>
`;

export const DIALECTS = `
<p>Four generators, four habits. This is what each writes, and which readers can see it — as of
${COMFY_READER_DATE}.</p>

<div class="table-scroll">
<table class="data">
  <thead><tr><th>Generator</th><th>Keyword</th><th>Carrier</th><th>Contents</th></tr></thead>
  <tbody>
    <tr><td>ComfyUI</td><td><code>workflow</code></td><td>tEXt</td><td>The node canvas you edited. This is what a drag-and-drop into ComfyUI restores.</td></tr>
    <tr><td>ComfyUI</td><td><code>prompt</code></td><td>tEXt</td><td>The flattened graph actually executed. ComfyUI can rebuild a canvas from this alone.</td></tr>
    <tr><td>ComfyUI (animated)</td><td>—</td><td><code>comf</code>, after IDAT</td><td>The same payloads, in a private chunk written only by the animated-PNG writer.</td></tr>
    <tr><td>A1111 / Forge</td><td><code>parameters</code></td><td>tEXt</td><td>One string: prompt, negative prompt, then steps, sampler, CFG, seed, size and model hash.</td></tr>
    <tr><td>A1111 / Forge / NovelAI</td><td>—</td><td>${term('lsb', 'pixel plane')}</td><td>The same settings string, written a bit at a time into the alpha or RGB low bit. Default on in NovelAI.</td></tr>
    <tr><td>InvokeAI</td><td><code>invokeai_metadata</code></td><td>tEXt</td><td>A JSON record of the generation.</td></tr>
    <tr><td>InvokeAI</td><td><code>invokeai_graph</code></td><td>tEXt</td><td>The executed graph.</td></tr>
    <tr><td>NovelAI</td><td><code>Comment</code>, <code>Software</code>, <code>Description</code></td><td>tEXt</td><td>Settings JSON, the generator's name, and the prompt.</td></tr>
  </tbody>
</table>
</div>

<h3>Where readers give up</h3>
<ul>
  <li>ComfyUI's front-end reader tests for <code>tEXt</code>, <code>comf</code> and <code>iTXt</code>. It
  has <strong>no branch for ${term('text', 'zTXt')} at all</strong> — a compressed text chunk is skipped
  in silence. regraft therefore reads zTXt and never writes it.</li>
  <li>Many readers stop at the ${term('idat', 'image data')}. The specification puts no ordering
  constraint on text chunks, and ComfyUI's own animated writer places them after it, so a recipe there is
  legal and routinely invisible.</li>
  <li>Readers that validate ${term('crc', 'checksums')} strictly throw the whole file away when one byte
  of image data is damaged — losing intact text alongside it.</li>
  <li>Nothing that reads chunks can see the ${term('lsb', 'pixel plane')}, because it is not in a chunk.</li>
</ul>

<h3>Two bugs the prediction reproduces</h3>
<ul>
  <li>ComfyUI's reader dispatches on the browser's guess at the file's type rather than on its actual
  bytes, so a PNG that a chat app renamed to <code>.jpg</code> is refused before it is opened. Renaming
  it back fixes it.</li>
  <li>One to three stray bytes after the end of the image make that reader throw an uncaught error. A
  graft drops a trailer that short for exactly this reason, and says so when it does.</li>
</ul>
`;

export const ABOUT = `
<p><strong>regraft</strong> recovers the prompt and workflow from an AI image — including from carriers
other tools skip — and writes them back into the file so the application you use can find them again.</p>

<p>It runs entirely in your browser. There is no account, no upload, no queue and no limit on how many
images you put through it. It has no runtime dependencies at all: the PNG reader and writer, the text
chunk codec, the scanline filters and the bit-plane engine are written from the specification rather
than borrowed, which is what makes it possible to read a file that a stricter library refuses.</p>

<p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
The rest of the catalogue — tools and sites, all of them free and most of them client-side — is at
<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>

<h3>Related</h3>
<ul>
  <li><a href="https://metascrub.benrichardson.dev" target="_blank" rel="noopener">metascrub</a> — removes
  metadata from PNG, WebP and JPEG. That is the job regraft deliberately does not do.</li>
  <li><a href="https://veilpix.benrichardson.dev" target="_blank" rel="noopener">veilpix</a> — hides an
  encrypted message inside an image's pixels, which is the same trick as the stealth plane put to a
  different use.</li>
</ul>

<h3>Source</h3>
<p><a href="https://github.com/ben-gy/regraft" target="_blank" rel="noopener">github.com/ben-gy/regraft</a></p>
`;
